import InfoIcon from "@mui/icons-material/Info";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { IconButton } from "@mui/material";
import CancelButton from "Common/Components/Buttons/CancelButton";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import LoadingSpinner from "components/LoadingSpinner";
import { MecordColors } from "Constants/Color";
import { DETAILS_BG, filterPopoverStyles, tableStyles } from "Constants/Style";
import DOMPurify from "dompurify";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  Container,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchDispatchId } from "slices/Warehouse/Dispatch/thunk";

const DispatchViewDetails = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { dispatch_id } = useParams();
  const [dispatchInfo, setDispatchInfo] = useState<any>(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [modalProducts, setModalProducts] = useState<any[]>([]);
  const [modalPackingNumber, setModalPackingNumber] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] =
    useState(false);
  const [additionalDetailsContent, setAdditionalDetailsContent] = useState("");
  const [additionalDetailsProductName, setAdditionalDetailsProductName] =
    useState("");

  useEffect(() => {
    if (dispatch_id) {
      const getDispatchInfo = async () => {
        setLoading(true);
        try {
          let response = await dispatch(fetchDispatchId(dispatch_id)).unwrap();

          setDispatchInfo(response?.data); // Make sure you're setting data here
        } catch (error) {
          console.error("Failed to fetch dispatch info:", error);
        } finally {
          setLoading(false);
        }
      };
      getDispatchInfo();
    }
  }, [dispatch_id]);

  // console.log("dispatchInfo : ", dispatchInfo);

  const calculateDiscountAmount = (
    netValue: number,
    discountType: string,
    discountValue: number,
  ) => {
    if (
      discountType === "%" ||
      discountType === "percentage" ||
      discountType === "percent"
    ) {
      return (netValue * discountValue) / 100;
    } else {
      return discountValue;
    }
  };
  // Get currency symbol from sales order or default to ₹
  const currencySymbol =
    dispatchInfo?.sales_order_product?.currency_symbol || "₹";

  // Calculate total discount amount by summing all dispatch products' discount_amount
  const totalDiscountAmount =
    dispatchInfo?.dispatch_products?.reduce((sum: number, prod: any) => {
      console.log("prod discount type => ", prod?.discount_type);
      console.log("prod discount amount => ", prod?.discount_amount);
      const discountAmount =
        calculateDiscountAmount(
          prod?.item_amount,
          prod?.discount_type,
          prod?.discount_amount,
        ) || 0;
      return sum + discountAmount;
    }, 0) || 0;

  const transportType =
    dispatchInfo?.transporter_type ||
    dispatchInfo?.transporter_info?.[0]?.transporter_type ||
    dispatchInfo?.transporter_info?.[0]?.transport_type ||
    dispatchInfo?.transporter_details?.transporter_type ||
    "-";

  const transportDetails =
    dispatchInfo?.transporter_details ||
    dispatchInfo?.transporter_info?.[0] ||
    {};

  // Normalize transport type to lowercase for comparison
  const normalizedTransportType = transportType?.toLowerCase() || "";

  // Self Pickup / Person fields
  const personName =
    transportDetails?.person_name ||
    dispatchInfo?.transporter_info?.[0]?.person_name ||
    "-";

  const personNumber =
    transportDetails?.person_number ||
    dispatchInfo?.transporter_info?.[0]?.person_number ||
    "-";

  // Courier fields
  const docketNumber = transportDetails?.docket_number || "-";
  const courierCompanyName = transportDetails?.company_name || "-";

  // Road fields
  const truckNumber = transportDetails?.truck_number || "-";
  const driverName = transportDetails?.driver_name || "-";
  const driverMobile = transportDetails?.driver_mobile || "-";

  // Rail fields
  const railCarrierName = transportDetails?.carrier_name || "-";
  const trainNumber = transportDetails?.train_number || "-";
  const bookingNumber = transportDetails?.booking_number || "-";
  const railOrigin = transportDetails?.origin || "-";

  // Air fields
  const airCarrierName = transportDetails?.carrier_name || "-";
  const flightNumber = transportDetails?.flight_number || "-";
  const awbNumber = transportDetails?.awb_number || "-";
  const airOrigin = transportDetails?.origin || "-";

  // Common fields (remarks can be in any type)
  const remarks = transportDetails?.remarks || "-";

  // Type detection
  const isSelfPickup = normalizedTransportType === "self pickup";
  const isCourier = normalizedTransportType === "courier";
  const isRoad = normalizedTransportType === "road";
  const isRail = normalizedTransportType === "rail";
  const isAir = normalizedTransportType === "air";

  // Check if transport section should be shown
  const hasTransportData =
    transportType !== "-" ||
    (isSelfPickup && (personName !== "-" || personNumber !== "-")) ||
    (isCourier && (docketNumber !== "-" || courierCompanyName !== "-")) ||
    (isRoad &&
      (truckNumber !== "-" || driverName !== "-" || driverMobile !== "-")) ||
    (isRail &&
      (railCarrierName !== "-" ||
        trainNumber !== "-" ||
        bookingNumber !== "-" ||
        railOrigin !== "-")) ||
    (isAir &&
      (airCarrierName !== "-" ||
        flightNumber !== "-" ||
        awbNumber !== "-" ||
        airOrigin !== "-"));

  return (
    <div className="page-content">
      <Container fluid>
        <Row id="invoiceList">
          <Col lg={12}>
            <Card>
              <Card.Header>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <Link
                      to="/warehouse-dispatch"
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      <i
                        className="bi bi-arrow-left"
                        style={{ fontSize: "1.2rem", cursor: "pointer" }}
                      />
                    </Link>
                    <h6 className="card-title mb-0">View Details</h6>
                  </div>
                  <div className="text-muted small">
                    <Link
                      to="/warehouse-dispatch"
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      Dispatch
                    </Link>{" "}
                    &gt; <span style={{ color: "inherit" }}>View Details</span>
                  </div>
                </div>
                <hr />
              </Card.Header>

              {loading ? (
                <LoadingSpinner />
              ) : (
                <Card.Body>
                  {/* View Details Section */}
                  <div className="mb-4">
                    <Row>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Dispatch No:</strong>{" "}
                          {dispatchInfo?.dispatch_no || "-"}
                        </p>
                      </Col>

                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Dispatch Date:</strong>{" "}
                          <FormattedDate date={dispatchInfo?.dispatch_date} />
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>So No:</strong>{" "}
                          {dispatchInfo?.sales_order_product
                            ?.sales_order_number || "-"}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Initiated By:</strong>{" "}
                          {dispatchInfo?.initiated_by ||
                            dispatchInfo?.dispatch_by ||
                            "-"}
                        </p>
                      </Col>
                      {/* {Array.isArray(dispatchInfo?.tags) &&
                        dispatchInfo.tags.length > 0 && ( */}
                      <Col md={6}>
                        <div className="d-flex align-items-start">
                          <strong className="me-2">Tags:</strong>

                          <div className="d-flex flex-wrap gap-2">
                            {(dispatchInfo?.tags || []).map((tag: string) => (
                              <span
                                key={tag}
                                className="badge bg-light text-dark rounded-2"
                                style={{
                                  border: "1px solid #dee2e6",
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Col>
                      {/* )} */}
                      {/* <Col md={6}>
                      <p className="mb-2">
                        <strong>Transfer Type:</strong>{" "}
                        {dispatchInfo?.transfer_type || "-"}
                      </p>
                    </Col> */}
                    </Row>
                  </div>

                  <hr
                    style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }}
                  />

                  {/* Customer Details Section */}
                  <div className="mb-4">
                    <h6
                      className="fw-bold mb-3"
                      style={{ color: "#495057", fontSize: "0.9rem" }}
                    >
                      Customer Details
                    </h6>
                    <div
                      className="p-4 rounded-3 mb-4"
                      style={{
                        backgroundColor: MecordColors?.LightThemeBGColor,
                      }}
                    >
                      <Row className="mb-3">
                        <Col md={6}>
                          <p>
                            <strong>Company Name :</strong>{" "}
                            {dispatchInfo?.customer_details?.company_name ||
                              "-"}
                          </p>
                          <p>
                            <strong>Company Email :</strong>{" "}
                            {dispatchInfo?.customer_details?.email || "-"}
                          </p>
                          <p>
                            <strong>Customer PO Number :</strong>{" "}
                            {dispatchInfo?.customer_details
                              ?.customer_po_number || "-"}
                          </p>
                          <p>
                            <strong>PO Document :</strong>{" "}
                            {dispatchInfo?.customer_details
                              ?.customer_po_documents &&
                            Array.isArray(
                              dispatchInfo?.customer_details
                                ?.customer_po_documents,
                            ) &&
                            dispatchInfo?.customer_details
                              ?.customer_po_documents.length > 0 ? (
                              <div>
                                {dispatchInfo?.customer_details?.customer_po_documents.map(
                                  (doc: string, index: number) => (
                                    <div key={index} className="mb-1">
                                      <a
                                        href={doc}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-underline"
                                      >
                                        View Document {index + 1}
                                      </a>
                                    </div>
                                  ),
                                )}
                              </div>
                            ) : dispatchInfo?.customer_details
                                ?.customer_po_document_url ? (
                              <a
                                href={
                                  dispatchInfo?.customer_details
                                    ?.customer_po_document_url
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-decoration-underline"
                              >
                                View Document
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </p>
                        </Col>

                        <Col md={6}>
                          <p>
                            <strong>Industry :</strong>{" "}
                            {dispatchInfo?.customer_details?.industry || "-"}
                          </p>
                          <p>
                            <strong>Company Phone No. :</strong>{" "}
                            {dispatchInfo?.customer_details?.phone_number ||
                              "-"}
                          </p>
                          <p>
                            <strong>PO Date :</strong>{" "}
                            {dispatchInfo?.customer_details
                              ?.customer_po_date ? (
                              <FormattedDate
                                date={
                                  dispatchInfo.customer_details.customer_po_date
                                }
                              />
                            ) : (
                              "-"
                            )}
                          </p>
                        </Col>
                      </Row>

                      {dispatchInfo?.customer_details?.contact_details?.length >
                        0 && (
                        <>
                          <hr />
                          <h6 className="fw-bold mb-3">
                            Additional Contact Details
                          </h6>
                          <Row>
                            {dispatchInfo.customer_details.contact_details.map(
                              (contact: any, index: number) => (
                                <Col md={6} key={index} className="mb-3">
                                  <p>
                                    <strong>Contact Name :</strong>{" "}
                                    {contact.name || "-"}
                                  </p>
                                  <p>
                                    <strong>Email :</strong>{" "}
                                    {contact.email || "-"}
                                  </p>
                                  <p>
                                    <strong>Phone Number :</strong>{" "}
                                    {contact.phone_number || "-"}
                                  </p>
                                </Col>
                              ),
                            )}
                          </Row>
                        </>
                      )}
                    </div>
                  </div>

                  <hr
                    style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }}
                  />

                  {/* Billing Details Section */}
                  <div className="mb-4">
                    <h6
                      className="fw-bold mb-3"
                      style={{ color: "#495057", fontSize: "0.9rem" }}
                    >
                      Billing Details
                    </h6>
                    <div
                      className="p-4 rounded-3"
                      style={{
                        backgroundColor: MecordColors?.LightThemeBGColor,
                      }}
                    >
                      <Row>
                        <Col md={6}>
                          <p className="fw-bold mb-1">Bill From :</p>
                          {dispatchInfo?.bill_from ? (
                            <p className="mb-0">
                              {dispatchInfo.bill_from.org_address}
                              <br />
                              {`${dispatchInfo.bill_from.org_city}, ${dispatchInfo.bill_from.org_state}, ${dispatchInfo.bill_from.org_country} - ${dispatchInfo.bill_from.org_pincode}`}
                            </p>
                          ) : (
                            <p className="text-muted mb-0">Not available</p>
                          )}
                        </Col>

                        <Col md={6}>
                          <p className="fw-bold mb-1">Bill To :</p>
                          {dispatchInfo?.bill_to ? (
                            <p className="mb-0">
                              {dispatchInfo.bill_to.address}
                              <br />
                              {`${dispatchInfo.bill_to.city}, ${dispatchInfo.bill_to.state}, ${dispatchInfo.bill_to.country} - ${dispatchInfo.bill_to.pincode}`}
                            </p>
                          ) : (
                            <p className="text-muted mb-0">Not available</p>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </div>

                  <hr
                    style={{ margin: "20px 0", borderTop: "1px solid #dee2e6" }}
                  />

                  {/* Transport Details Section */}
                  {hasTransportData && (
                    <>
                      <div className="mb-4">
                        <h6
                          className="mb-3"
                          style={{ color: "#495057", fontWeight: "600" }}
                        >
                          {isSelfPickup
                            ? "Self Pickup Details"
                            : isCourier
                              ? "Courier Details"
                              : isRoad
                                ? "Road Transport Details"
                                : isRail
                                  ? "Rail Transport Details"
                                  : isAir
                                    ? "Air Transport Details"
                                    : "Transport Details"}
                        </h6>
                        <Row>
                          <Col md={6}>
                            <p className="mb-2">
                              <strong>Transporter Type:</strong> {transportType}
                            </p>
                          </Col>

                          {/* Self Pickup */}
                          {isSelfPickup && (
                            <>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Person Name:</strong> {personName}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Person Number:</strong> {personNumber}
                                </p>
                              </Col>
                            </>
                          )}

                          {/* Courier */}
                          {isCourier && (
                            <>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Docket Number:</strong> {docketNumber}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Company Name:</strong>{" "}
                                  {courierCompanyName}
                                </p>
                              </Col>
                            </>
                          )}

                          {/* Road */}
                          {isRoad && (
                            <>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Transporter Name:</strong>{" "}
                                  {transportDetails?.transporter_name || "-"}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Truck Number:</strong> {truckNumber}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Driver Name:</strong> {driverName}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Driver Mobile:</strong> {driverMobile}
                                </p>
                              </Col>
                            </>
                          )}

                          {/* Rail */}
                          {isRail && (
                            <>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Carrier Name:</strong>{" "}
                                  {railCarrierName}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Train Number:</strong> {trainNumber}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Booking Number:</strong>{" "}
                                  {bookingNumber}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Origin:</strong> {railOrigin}
                                </p>
                              </Col>
                            </>
                          )}

                          {/* Air */}
                          {isAir && (
                            <>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Carrier Name:</strong>{" "}
                                  {airCarrierName}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Flight Number:</strong> {flightNumber}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>AWB Number:</strong> {awbNumber}
                                </p>
                              </Col>
                              <Col md={6}>
                                <p className="mb-2">
                                  <strong>Origin:</strong> {airOrigin}
                                </p>
                              </Col>
                            </>
                          )}

                          {/* Remarks (common for all types) */}
                          {remarks !== "-" && (
                            <Col md={12}>
                              <p className="mb-2">
                                <strong>Remarks:</strong> {remarks}
                              </p>
                            </Col>
                          )}
                        </Row>
                      </div>
                      <hr
                        style={{
                          margin: "20px 0",
                          borderTop: "1px solid #dee2e6",
                        }}
                      />
                    </>
                  )}
                  <div className="mb-4">
                    <h6
                      className="mb-3"
                      style={{ color: "#495057", fontWeight: "600" }}
                    >
                      Shipping Address
                    </h6>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Address:</strong>{" "}
                          {dispatchInfo?.shipping_address_details.address}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>City:</strong>{" "}
                          {dispatchInfo?.shipping_address_details.city}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>State:</strong>{" "}
                          {dispatchInfo?.shipping_address_details.state}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Country:</strong>{" "}
                          {dispatchInfo?.shipping_address_details.country}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Pincode:</strong>{" "}
                          {dispatchInfo?.shipping_address_details.pin_code}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Contact Number:</strong>{" "}
                          {dispatchInfo?.shipping_address_details.phone_number}
                        </p>
                      </Col>
                    </Row>
                  </div>

                  <div>
                    <h5 className="fw-bold mb-3">Product Details</h5>
                    <div
                      className="table-responsive p-3 mb-4"
                      style={{
                        backgroundColor: MecordColors.ActiveBackground,
                        borderRadius: "10px",
                      }}
                    >
                      <div className="overflow-auto" style={{}}>
                        <table
                          style={{
                            width: "100%",
                            minWidth: "2000px",
                            borderCollapse: "collapse",
                            fontSize: "0.75rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <thead
                            style={{
                              backgroundColor: MecordColors.LightGrayColor,
                            }}
                          >
                            <tr>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Sr No.
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Packing No.
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Products
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Make
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Model
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Product Code
                              </th>
                              {/* <th
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                                textAlign: "left",
                              }}
                            >
                              Serviceable
                            </th> */}
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Ordered Quantity
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Quantity
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Unit
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Unit Price
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Rate in Currency
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Gross Value
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Discount/Flat
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Discount Value
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Net Value
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                GST(%)
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                SGST(%)
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                CGST(%)
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                IGST(%)
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                GST Value
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                }}
                              >
                                Total
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "center",
                                }}
                              >
                                Additional Details
                              </th>
                              <th
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                  textAlign: "left",
                                  minWidth: "100px",
                                  position: "sticky",
                                  right: 0,
                                  backgroundColor: MecordColors.LightGrayColor,
                                  zIndex: 2,
                                }}
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody style={{ backgroundColor: "#fff" }}>
                            {dispatchInfo?.dispatch_products?.length > 0 ? (
                              dispatchInfo.dispatch_products.map(
                                (prod: any, idx: number) => (
                                  <tr key={prod.product_id || idx}>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {idx + 1}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.packing?.packing_number || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {prod?.product_name || "-"}
                                      </div>
                                      <div
                                        style={{
                                          color: "#666",
                                          fontSize: "0.65rem",
                                        }}
                                      >
                                        SKU:{" "}
                                        {prod?.product_sku ||
                                          prod?.sku ||
                                          "N/A"}{" "}
                                        | HSN Code: {prod?.hsn_code || "N/A"}
                                      </div>
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.make_name || "N/A"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.model_name || "N/A"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.product_code || "N/A"}
                                    </td>
                                    {/* <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #eee",
                                    textAlign: "center",
                                  }}
                                >
                                  {prod?.serviceable || prod?.is_serviceable ? "Yes" : "No"}
                                </td> */}
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.order_quantity || 0}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.quantity || 0}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.unit_name || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {currencySymbol}{" "}
                                      {formatIndianAmount(prod?.unit_price)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {currencySymbol}{" "}
                                      {formatIndianAmount(
                                        prod?.rate_in_currency,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {currencySymbol}{" "}
                                      {formatIndianAmount(
                                        prod?.gross_value || prod?.item_amount,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {(() => {
                                        // discount_value is now the RAW input value (10 for 10% or 500 for ₹500)
                                        const discountValueInput =
                                          parseFloat(prod?.discount_amount) ||
                                          0;
                                        console.log("prod => ", prod);
                                        const discountType =
                                          prod?.discount_type || "currency";

                                        if (
                                          discountType === "%" ||
                                          discountType === "percentage"
                                        ) {
                                          // Display raw percentage value
                                          return `${formatIndianAmount(discountValueInput)}%`;
                                        } else {
                                          // Display "Flat" for currency/flat discounts
                                          return "Flat";
                                        }
                                      })()}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {(() => {
                                        // Calculate the actual discount amount from raw discount_value
                                        const discountValueInput =
                                          parseFloat(prod?.discount_value) ||
                                          parseFloat(prod?.discount_amount);
                                        const discountType =
                                          prod?.discount_type || "currency";
                                        const grossValue =
                                          parseFloat(
                                            prod?.gross_value ||
                                              prod?.item_amount ||
                                              0,
                                          ) || 0;

                                        let calculatedDiscountAmount = 0;
                                        if (
                                          discountType === "%" ||
                                          discountType === "percentage"
                                        ) {
                                          // Calculate: (gross * percentage) / 100
                                          calculatedDiscountAmount =
                                            (grossValue * discountValueInput) /
                                            100;
                                        } else {
                                          // Currency discount - use raw value directly (capped at gross)
                                          calculatedDiscountAmount = Math.min(
                                            discountValueInput,
                                            grossValue,
                                          );
                                        }

                                        return `${currencySymbol}${formatIndianAmount(calculatedDiscountAmount)}`;
                                      })()}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {currencySymbol}{" "}
                                      {formatIndianAmount(
                                        prod?.net_taxable_amount ||
                                          prod?.net_taxable_value ||
                                          prod?.net_value ||
                                          0,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.gst_percent ||
                                        prod?.gst_percentage ||
                                        "-"}
                                      {prod?.gst_percent || prod?.gst_percentage
                                        ? "%"
                                        : ""}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.sgst_percent ||
                                        prod?.sgst_percentage ||
                                        "-"}
                                      {prod?.sgst_percent ||
                                      prod?.sgst_percentage
                                        ? "%"
                                        : ""}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.cgst_percent ||
                                        prod?.cgst_percentage ||
                                        "-"}
                                      {prod?.cgst_percent ||
                                      prod?.cgst_percentage
                                        ? "%"
                                        : ""}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {prod?.igst_percent ||
                                        prod?.igst_percentage ||
                                        "-"}
                                      {prod?.igst_percent ||
                                      prod?.igst_percentage
                                        ? "%"
                                        : ""}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {currencySymbol}{" "}
                                      {formatIndianAmount(
                                        (() => {
                                          // Try to use existing gst_amount/gst_value first
                                          if (
                                            prod?.gst_amount ||
                                            prod?.gst_value
                                          ) {
                                            return Number(
                                              prod?.gst_amount ||
                                                prod?.gst_value ||
                                                0,
                                            );
                                          }
                                          // Otherwise calculate manually
                                          const netTaxable = Number(
                                            prod?.net_taxable_amount ||
                                              prod?.net_taxable_value ||
                                              prod?.net_value ||
                                              0,
                                          );
                                          const gstPercent = Number(
                                            prod?.gst_percent ||
                                              prod?.gst_percentage ||
                                              0,
                                          );
                                          if (netTaxable && gstPercent) {
                                            return (
                                              (netTaxable * gstPercent) / 100
                                            );
                                          }
                                          return 0;
                                        })(),
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {currencySymbol}{" "}
                                      {formatIndianAmount(
                                        prod?.total || prod?.total_amount || 0,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        textAlign: "center",
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      {!!prod?.additional_details && (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setAdditionalDetailsContent(
                                              prod.additional_details,
                                            );
                                            setShowAdditionalDetailsModal(true);
                                          }}
                                        >
                                          <InfoOutlined
                                            style={{
                                              color: MecordColors.ThemeBGColor,
                                              fontSize: "20px",
                                            }}
                                          />
                                        </IconButton>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        minWidth: "100px",
                                        position: "sticky",
                                        right: 0,
                                        backgroundColor: "white",
                                        zIndex: 2,
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <button
                                        className="btn btn-sm"
                                        style={{
                                          backgroundColor:
                                            MecordColors?.ThemeBGColor,
                                          color: "#FFF",
                                        }}
                                        onClick={() => {
                                          // Filter packing_products to show only those matching this dispatch product row
                                          const allPackingProducts =
                                            prod?.packing?.packing_products ||
                                            [];
                                          const salesOrderProductItemId =
                                            prod?.sales_order_product_item_id;
                                          const productId = prod?.product_id;

                                          // Filter by sales_order_product_item_id if available, otherwise by product_id
                                          const filteredProducts =
                                            salesOrderProductItemId
                                              ? allPackingProducts.filter(
                                                  (p: any) =>
                                                    p.sales_order_product_item_id ===
                                                    salesOrderProductItemId,
                                                )
                                              : productId
                                                ? allPackingProducts.filter(
                                                    (p: any) =>
                                                      p.product_id ===
                                                      productId,
                                                  )
                                                : allPackingProducts;

                                          setModalProducts(filteredProducts);
                                          setModalPackingNumber(
                                            prod?.packing_number ||
                                              prod?.packing_no ||
                                              prod?.packing?.packing_number ||
                                              "",
                                          );
                                          setShowProductsModal(true);
                                        }}
                                        title="View Products"
                                      >
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ),
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={22}
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #eee",
                                    textAlign: "center",
                                    color: "#666",
                                    paddingTop: "20px",
                                    paddingBottom: "20px",
                                  }}
                                >
                                  No products added yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Products Modal */}
                    <Modal
                      show={showProductsModal}
                      onHide={() => setShowProductsModal(false)}
                      size="xl"
                      centered
                    >
                      <Modal.Header closeButton>
                        <Modal.Title>View Product Details</Modal.Title>
                      </Modal.Header>
                      <Modal.Body style={{ overflowX: "auto", width: "100%" }}>
                        <Table className="table align-middle">
                          <thead
                            style={{
                              backgroundColor: MecordColors.LightGrayColor,
                            }}
                          >
                            <tr>
                              <th style={{ minWidth: "80px" }}>Sr. No.</th>
                              <th style={{ minWidth: "150px" }}>
                                Product Name
                              </th>
                              <th style={{ minWidth: "150px" }}>
                                Product Code
                              </th>
                              <th style={{ minWidth: "150px" }}>Make</th>
                              <th style={{ minWidth: "150px" }}>Model</th>
                              <th style={{ minWidth: "150px" }}>Serial No.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalProducts && modalProducts.length ? (
                              modalProducts.map((p: any, i: number) => {
                                const serial =
                                  p.serial_number ??
                                  p.serial_no ??
                                  (p.serials
                                    ? Array.isArray(p.serials)
                                      ? p.serials.join(", ")
                                      : p.serials
                                    : null) ??
                                  String(i + 1);
                                const productName =
                                  p?.product_snapshot?.product_name ||
                                  p?.product_snapshot?.item_name ||
                                  p?.product_name ||
                                  "-";
                                return (
                                  <tr key={p.packing_product_id || p.product_id || i}>
                                    <td>{i + 1}</td>
                                    <td>{productName}</td>
                                    <td>
                                      {p?.product_snapshot?.product_code ||
                                        p?.product_code ||
                                        "-"}
                                    </td>
                                    <td>
                                      {p?.product_snapshot?.make_name ||
                                        p?.product_snapshot?.make ||
                                        p.make_name ||
                                        "-"}
                                    </td>
                                    <td>
                                      {p?.product_snapshot?.model_name ||
                                        p?.product_snapshot?.model ||
                                        p?.model_name ||
                                        "-"}
                                    </td>
                                    <td>{serial}</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="text-center text-muted"
                                >
                                  No products available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </Modal.Body>
                      <Modal.Footer>
                        <div className="d-flex justify-content-center w-100">
                          <CancelButton
                            showConfirmation={false}
                            onClick={() => setShowProductsModal(false)}
                            disabled={isSubmitting}
                          />
                        </div>
                      </Modal.Footer>
                    </Modal>

                    {/* Additional Details Modal (View only) */}
                    <Modal
                      show={showAdditionalDetailsModal}
                      onHide={() => {
                        setShowAdditionalDetailsModal(false);
                        setAdditionalDetailsContent("");
                      }}
                      centered
                      className="modal-lg"
                      style={{ zIndex: 1060 }}
                    >
                      <Modal.Header closeButton>
                        <Modal.Title style={{ fontSize: "1rem", fontWeight: "600" }}>
                          Additional Details
                        </Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <div
                          className="p-3 border rounded bg-light"
                          style={{
                            minHeight: "100px",
                            maxHeight: "400px",
                            overflowY: "auto",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              additionalDetailsContent ||
                                "No additional details provided.",
                            ),
                          }}
                        />
                      </Modal.Body>
                      <Modal.Footer>
                        <Button
                          variant="secondary"
                          onClick={() => setShowAdditionalDetailsModal(false)}
                        >
                          Close
                        </Button>
                      </Modal.Footer>
                    </Modal>
                  </div>

                  <hr />
                  <Row className="g-4">
                    <Col md={6}>
                      <h5 className="mb-3">Terms & Conditions</h5>
                      <div
                        className="p-3"
                        style={{
                          backgroundColor: "#fff",
                          borderRadius: "10px",
                          border: "1px solid #eee",
                        }}
                      >
                        {(() => {
                          // Group terms by type to handle multiple options per type
                          const groupedTerms: Record<string, string[]> = {};
                          dispatchInfo?.sales_order_product?.terms_and_conditions?.forEach(
                            (item: any) => {
                              if (item.type) {
                                if (!groupedTerms[item.type]) {
                                  groupedTerms[item.type] = [];
                                }
                                // Handle both string and array options
                                if (Array.isArray(item.options)) {
                                  item.options.forEach((opt: string) => {
                                    if (
                                      opt &&
                                      !groupedTerms[item.type].includes(opt)
                                    ) {
                                      groupedTerms[item.type].push(opt);
                                    }
                                  });
                                } else if (item.options) {
                                  // If options is a string, check if it's newline-separated
                                  const options = item.options
                                    .split("\n")
                                    .filter((opt: string) => opt.trim());
                                  options.forEach((opt: string) => {
                                    if (
                                      opt &&
                                      !groupedTerms[item.type].includes(opt)
                                    ) {
                                      groupedTerms[item.type].push(opt);
                                    }
                                  });
                                }
                              }
                            },
                          );

                          if (Object.keys(groupedTerms).length === 0) {
                            return (
                              <p
                                className="text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                No terms and conditions available
                              </p>
                            );
                          }

                          return Object.entries(groupedTerms).map(
                            ([type, options], idx) => (
                              <div key={idx} className="mb-3">
                                <strong
                                  style={{
                                    fontSize: "0.75rem",
                                    display: "block",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  {type}
                                </strong>
                                <ul
                                  style={{
                                    fontSize: "0.7rem",
                                    color: "#666",
                                    marginLeft: "20px",
                                    paddingLeft: "0",
                                    listStyle: "disc",
                                  }}
                                >
                                  {options.map(
                                    (option: string, optIdx: number) => (
                                      <li
                                        key={optIdx}
                                        style={{ marginBottom: "4px" }}
                                      >
                                        {option}
                                      </li>
                                    ),
                                  )}
                                </ul>
                                {idx < Object.keys(groupedTerms).length - 1 && (
                                  <hr
                                    style={{
                                      width: "100%",
                                      marginTop: "12px",
                                    }}
                                  />
                                )}
                              </div>
                            ),
                          );
                        })()}
                      </div>
                    </Col>

                    <Col md={6}>
                      <h5 className="mb-3">Total Summary</h5>
                      <div
                        className="p-3"
                        style={{
                          backgroundColor: MecordColors.ActiveBackground,
                          borderRadius: "10px",
                        }}
                      >
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Sub Total (Before Discount) :</strong>
                            </p>
                          </Col>
                          <Col md={6} className="text-end">
                            <p>
                              {currencySymbol}{" "}
                              {formatIndianAmount(
                                dispatchInfo?.subtotal ||
                                  dispatchInfo?.sub_total_before_discount ||
                                  0,
                              )}
                            </p>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Total Discount Value :</strong>
                            </p>
                          </Col>
                          <Col md={6} className="text-end">
                            <p>
                              {currencySymbol}{" "}
                              {formatIndianAmount(
                                totalDiscountAmount ||
                                  dispatchInfo?.total_product_discount ||
                                  dispatchInfo?.total_discount_value ||
                                  dispatchInfo?.total_item_discounts ||
                                  0,
                              )}
                            </p>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Net Taxable Value :</strong>
                            </p>
                          </Col>
                          <Col md={6} className="text-end">
                            <p>
                              {currencySymbol}{" "}
                              {formatIndianAmount(
                                dispatchInfo?.net_taxable_amount ||
                                  dispatchInfo?.net_taxable_value ||
                                  0,
                              )}
                            </p>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Total GST Value :</strong>
                            </p>
                          </Col>
                          <Col md={6} className="text-end">
                            <p>
                              {currencySymbol}{" "}
                              {formatIndianAmount(
                                dispatchInfo?.total_gst_amount ||
                                  dispatchInfo?.total_gst_value ||
                                  0,
                              )}
                            </p>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Grand Total Amount :</strong>
                            </p>
                          </Col>
                          <Col md={6} className="text-end">
                            <p className="fw-bold">
                              {currencySymbol}{" "}
                              {formatIndianAmount(
                                dispatchInfo?.total_amount ||
                                  dispatchInfo?.grand_total_amount ||
                                  dispatchInfo?.total ||
                                  0,
                              )}
                            </p>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>

                  {/* Notes & Attachments Section */}
                  <div className="mt-3">
                    <h5
                      className="mb-3"
                      style={{ fontSize: "16px", fontWeight: 600 }}
                    >
                      Notes & Attachments
                    </h5>
                    <Table bordered className="bg-white">
                      <tbody>
                        <tr>
                          <td style={{ width: "25%", fontWeight: 500 }}>
                            Notes
                          </td>
                          <td
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "12px",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {dispatchInfo?.note || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 500 }}>Attachment</td>
                          <td style={{ fontSize: "14px" }}>
                            {(() => {
                              if (
                                dispatchInfo?.attachments &&
                                Array.isArray(dispatchInfo.attachments) &&
                                dispatchInfo.attachments.length > 0
                              ) {
                                return (
                                  <div className="d-flex flex-column gap-2">
                                    {dispatchInfo.attachments.map(
                                      (
                                        attachment: string | any,
                                        index: number,
                                      ) => {
                                        const fileUrl =
                                          typeof attachment === "string"
                                            ? attachment
                                            : attachment?.file_url ||
                                              attachment?.file_path;
                                        const fileName =
                                          typeof attachment === "string"
                                            ? attachment.split("/").pop()
                                            : attachment?.file_name ||
                                              fileUrl?.split("/").pop();

                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center justify-content-between p-2 border rounded"
                                            style={{
                                              backgroundColor: "#f8f9fa",
                                            }}
                                          >
                                            <span
                                              className="text-truncate"
                                              style={{
                                                fontSize: "0.875rem",
                                                maxWidth: "100%",
                                                color: "#0d6efd",
                                                cursor: "pointer",
                                              }}
                                              title={fileName || "Download"}
                                              onClick={() => {
                                                if (fileUrl) {
                                                  window.open(
                                                    fileUrl,
                                                    "_blank",
                                                  );
                                                }
                                              }}
                                            >
                                              {fileName || "Download"}
                                            </span>
                                          </div>
                                        );
                                      },
                                    )}
                                  </div>
                                );
                              } else {
                                return "-";
                              }
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  {/* {dispatchInfo?.note && (
                  <Row className="mb-4">
                    <Col md={12}>
                      <h6 className="fw-bold mb-2" style={{ color: "#495057", fontSize: "0.9rem" }}>Notes</h6>
                      <div className="p-3 border rounded bg-light">
                        {dispatchInfo.note}
                      </div>
                    </Col>
                  </Row>
                )} */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "20px",
                    }}
                  >
                    <CancelButton
                      showConfirmation={false}
                      onClick={() => {
                        navigate("/warehouse-dispatch");
                      }}
                    />
                  </div>
                </Card.Body>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DispatchViewDetails;
